/**
 * SyncIndicator - tiny status pill showing online/offline + pending queue count.
 * Shows in the header so the user knows when something is awaiting upload.
 */
import React from 'react';
import { Cloud, CloudOff, RefreshCw } from 'lucide-react';
import useOfflineSync from '@/hooks/useOfflineSync';

export const SyncIndicator = ({ compact = false }) => {
  const { online, pendingCount, flushing, flushNow } = useOfflineSync();

  // Nothing to surface in the common case
  if (online && pendingCount === 0 && !flushing) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    if (online && pendingCount > 0) flushNow();
  };

  const label = !online
    ? `Offline${pendingCount > 0 ? ` · ${pendingCount} pending` : ''}`
    : flushing
      ? 'Syncing…'
      : `${pendingCount} pending`;

  const tone = !online
    ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    : flushing
      ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
      : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="sync-indicator"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${tone} hover:brightness-110 transition`}
      title={!online ? 'You are offline — changes will sync when you reconnect' : 'Tap to sync now'}
    >
      {!online ? <CloudOff className="w-3.5 h-3.5" />
        : flushing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        : <Cloud className="w-3.5 h-3.5" />}
      {!compact && <span>{label}</span>}
    </button>
  );
};

export default SyncIndicator;
